import React, {
  useEffect,
  forwardRef,
  useRef,
  useImperativeHandle,
  useState,
  useCallback,
} from 'react';
import { useSelector } from 'react-redux';
import * as THREE from 'three';
import { DotGroup, Dot, ButtonNext, ButtonBack } from 'pure-react-carousel';
import { Swipeable } from 'react-swipeable';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import ExternalLink from '../../Common/ExternalLink/ExternalLink';
import VenetianBlindsText from '../../Common/VenetianBlindsText/VenetianBlindsText';
import { linkTypes, scene3DAnimationType, swipeDirection, swipeType } from '../../../models/Models';
import { useSwipeEvent } from '../../../models/Hooks';

import './Scene3D.scss';
import { PMREMGenerator } from 'three';
import { ADAARIARoles } from '../../../models/ADA';

const Scene3D = forwardRef(
  ({ cardData: card, onGoToNextSlide, onGoToPrevSlide, isInline, size }, ref) => {
    let [currentView, setCurrentView] = useState(1);
    let [animationPlaying, setAnimationPlaying] = useState(false);
    let [model, setModel] = useState(null);
    let [loadingP, setLoadingP] = useState(0);
    let scene = useRef(new THREE.Scene());
    let [isSceneSetup, setIsSceneSetup] = useState(false);
    const menuOpened = useSelector(state => state.data.menuOpened);
    scene.current.background = new THREE.Color(0xffffff);
    const renderer = useRef(null);
    const camera = useRef(null);
    const canvasRef = useRef(null);
    const controlRef = useRef(null);
    const animationSpeed = 0.75;

    const scale = 62;
    let resizeTimer = null;
    let animationTimer = null;
    const views = [
      {
        rotation: 0,
        position: { x: 0, y: 0, z: 0 },
      },
      {
        rotation: -Math.PI / 3,
        position: { x: 0, y: 0, z: 0 },
      },
      {
        rotation: (-5 * Math.PI) / 6,
        position: { x: 0, y: 0, z: 0 },
      },
    ];

    const objectRef = useRef(null);
    const sceneRef = useRef(null);
    const activeSlideKey = useSelector(state => state.data.activeSlideKey);
    const configData = useSelector(state => state.config);

    const sceneSetup = useCallback(() => {
      renderer.current = new THREE.WebGLRenderer({ antialias: true, canvas: canvasRef.current });
    
      var pmrem = new PMREMGenerator(renderer.current);

      const width = size.width;
      let elSize;
      if (width >= 768) {
        elSize = width / 2 > 600 ? 600 : width / 2;
        camera.current = new THREE.PerspectiveCamera(
          75, // fov = field of view
          1, // aspect ratio
          0.1, // near plane
          300 // far plane
        );
      } else {
        elSize = width > 600 ? 600 : width;
        camera.current = new THREE.PerspectiveCamera(
          75, // fov = field of view
          1, // aspect ratio
          0.1, // near plane
          300 // far plane
        );
      }

      renderer.current.setSize(elSize, elSize);

      const dirLight1 = new THREE.DirectionalLight(0xffffff, 5);
      dirLight1.position.set(0, 0, 5);
      scene.current.add(dirLight1);
      const dirLight2 = new THREE.DirectionalLight(0xffffff, 5);
      dirLight2.position.set(-5, 3, -2);
      scene.current.add(dirLight2);
      scene.current.add(new THREE.AmbientLight(0xffffff, 10));

      camera.current.position.x = 0;
      camera.current.position.y = 0;
      camera.current.position.z = 6;
      sceneRef.current.appendChild(renderer.current.domElement); // mount using React ref

      renderer.current.render(scene.current, camera.current);
      window.addEventListener('resize', handleWindowResize);

      const loader = new GLTFLoader();

      // load a resource
      loader.load(
        // resource URL relative to the /public/index.html of the app
        configData.basePath + configData.apiMediaPath + card.Object,
        // '/verizon-9-digital-museum/gltf/' + card.Object,
        // called when resource is loaded
        object => {
          console.log('obj: ', object);
          scene.current.add(object.scene);
          object.scene.scale.set(scale, scale, scale);

          // make this element available inside of the whole component to do any animation later
          //  object.scene.rotation.z = Math.PI / 2;
          object.scene.rotation.y = views[0].rotation;
          object.scene.position.x = views[0].position.x;
          object.scene.position.y = views[0].position.y;
          object.scene.position.z = views[0].position.z;
          setModel(object.scene);

          renderer.current.render(scene.current, camera.current);
        },
        // called when loading is in progress
        xhr => {
          const loadingPercentage = Math.ceil((xhr.loaded / xhr.total) * 100);
          setLoadingP(loadingPercentage);
          console.log(loadingPercentage + '% loaded');
        },
        // called when loading has errors
        error => {
          console.log('An error happened: ' + error);
        }
      );
    });

    const handleWindowResize = useCallback(() => {
      if (resizeTimer) {
        clearTimeout(resizeTimer);
      }
      resizeTimer = setTimeout(() => {
        const width = sceneRef.current.clientWidth;
        let size;
        if (width >= 768) {
          size = width / 2 > 600 ? 600 : width / 2;
        } else {
          size = width > 600 ? 600 : width;
        }
        renderer.current.setSize(size, size );
        camera.current.updateProjectionMatrix();
      }, 50);
    }, [sceneRef]);

    const scheduleAnimation = useCallback(
      (position, rotation) => {
        let duration = 500; //ms
        let frame = 30; //ms
        let step = 0;
        let rStep = (rotation - model.rotation.y) / (duration / frame);
        let xStep = (position.x - model.position.x) / (duration / frame);
        let yStep = (position.y - model.position.y) / (duration / frame);
        let zStep = (position.z - model.position.z) / (duration / frame);

        return new Promise(resolve => {
          if (!animationPlaying) {
            animationTimer = setInterval(() => {
              setAnimationPlaying(true);
              model.rotation.y += rStep;
              model.position.x += xStep;
              model.position.y += yStep;
              model.position.z += zStep;
              step++;

              if (step * frame >= duration) {
                model.rotation.y = rotation;
                model.position.x = position.x;
                model.position.y = position.y;
                model.position.z = position.z;
                setTimeout(() => setAnimationPlaying(false), 100);

                clearInterval(animationTimer);
                resolve(true);
              }

              renderer.current.render(scene.current, camera.current);
            }, frame);
          } else {
            resolve(false);
          }
        });
      },
      [model, animationPlaying]
    );

    const reset = useCallback(() => {
      if (model && renderer.current && scene.current && camera.current) {
        model.rotation.y = views[0].rotation;
        model.position.x = views[0].position.x;
        model.position.y = views[0].position.y;
        model.position.z = views[0].position.z;
        setCurrentView(1);
        renderer.current.render(scene.current, camera.current);
      }
    }, [model]);

    const animate = useCallback(
      direction => {
        if (!animationPlaying && model) {
          let nextView;
          if (direction === scene3DAnimationType.FORWARD && currentView < 3) {
            nextView = currentView + 1;
          }
          if (direction === scene3DAnimationType.BACKWARD && currentView > 1) {
            nextView = currentView - 1;
          }
          scheduleAnimation(views[nextView - 1].position, views[nextView - 1].rotation).then(r => {
            if (r) {
              setCurrentView(nextView);
            }
          });
        }
      },
      [model, animationPlaying, currentView, scheduleAnimation]
    );

    const handleSwipeDown = useCallback(() => {
      if (animationPlaying) return;
      if (currentView === 1 || !model) {
        onGoToPrevSlide();
        reset();
      } else {
        animate(scene3DAnimationType.BACKWARD);
      }
    }, [currentView, onGoToPrevSlide, animate, model, animationPlaying]);

    const handleSwipeUp = useCallback(() => {
      if (animationPlaying) return;
      if (currentView === 3 || !model) {
        onGoToNextSlide();
        reset();
      } else {
        animate(scene3DAnimationType.FORWARD);
      }
    }, [currentView, onGoToNextSlide, animate, model, animationPlaying]);

    useEffect(() => {
      if (!isSceneSetup) {
        console.log('set scene');
        setIsSceneSetup(true);
        sceneSetup();
      }

      if (activeSlideKey !== card.Key) {
        setTimeout(() => {
          setCurrentView(1);
          if (model) {
            // model.rotation.z = -Math.PI / 2;
            model.rotation.y = views[0].rotation;
            model.position.x = views[0].position.x;
            model.position.y = views[0].position.y;
            model.position.z = views[0].position.z;
          }
        }, animationSpeed * 1000);
      }
      return () => {
        // window.cancelAnimationFrame(requestID);
        window.removeEventListener('resize', handleWindowResize);
        clearInterval(animationTimer);
        if (model && model.dispose) model.dispose();
      };
    }, [handleWindowResize, model, activeSlideKey, card]);

    const handleSwipe = useCallback(
      e => {
        if (!e) {
          return;
        }
        if (e.dir === swipeDirection.RIGHT && e.origin === swipeType.SCROLL) {
          if (currentView < 3) {
            console.log('track forward');
            animate(scene3DAnimationType.FORWARD);
          }
        }
        if (e.dir === swipeDirection.LEFT && e.origin === swipeType.SCROLL) {
          if (currentView > 1) {
            console.log('track back');
            animate(scene3DAnimationType.BACKWARD);
          }
        }
      },
      [currentView]
    );

    useSwipeEvent(objectRef.current, handleSwipe);

    useEffect(() => {
      if (activeSlideKey === card.Key) {
        setAnimationPlaying(true);
        setTimeout(() => setAnimationPlaying(false), animationSpeed * 1000);
      }
    }, [activeSlideKey]);

    useImperativeHandle(ref, () => ({
      handleSwipeUpRef() {
        handleSwipeUp();
      },
      handleSwipeDownRef() {
        handleSwipeDown();
      },
    }));

    const handleNextClicked = () => {
      animate(scene3DAnimationType.FORWARD);
    };

    const handlePrevClicked = () => {
      animate(scene3DAnimationType.BACKWARD);
    };

    const handleDotClicked = index => {
      if (index !== currentView - 1) {
        scheduleAnimation(views[index].position, views[index].rotation).then(r => {
          if (r) {
            setCurrentView(index + 1);
          }
        });
      }
    };

    const renderDots = props => {
      if (!props) {
        return null;
      }
      const { currentSlide, totalSlides, visibleSlides, showAsSelectedForCurrentSlideOnly } = props;

      const dots = [];
      for (let i = 0; i < totalSlides; i += 1) {
        const multipleSelected = i >= currentSlide && i < currentSlide + visibleSlides;
        const singleSelected = i === currentSlide;
        const selected = showAsSelectedForCurrentSlideOnly ? singleSelected : multipleSelected;
        const slide = i >= totalSlides - visibleSlides ? totalSlides - visibleSlides : i;
        const handleClick = selected
          ? null
          : () => {
              handleDotClicked(i);
            };
        dots.push(
          <div className="dotwrap" key={i} onClick={handleClick}>
            <Dot slide={slide} selected={selected} disabled={true}>
              <span />
            </Dot>
          </div>
        );
      }
      return dots;
    };

    const onSwipeLeft = () => {
      if (currentView < 3) {
        console.log('swipe left');
        handleSwipeUp();
      }
    };

    const onSwipeRight = () => {
      if (currentView > 1) {
        console.log('swipe right');
        handleSwipeDown();
      }
    };

    var classname = 'scene-3D ' + (isInline ? 'inline' : '');

    var inner = (
      <>
        {isInline && currentView < 3 ? (
        
            <div className="btn-next-flexbox">
              <div className="btn-next-flexcontainer">
                <div className="btn-next-container" onClick={handleNextClicked}>
                  <ButtonNext />
                </div>
              </div>
            </div>
        
        ) : null}
      {isInline && currentView > 1 ? (
            <div className="btn-back-flexbox">
            <div className="btn-back-flexcontainer">
              <div className="btn-back-container" onClick={handlePrevClicked}>
                <ButtonBack />
              </div>
            </div>
        </div>
        ) : null}


        <div className="scene-ref" ref={sceneRef} alt={card.AltText3D}>
          <canvas ref={canvasRef}></canvas>
          {loadingP < 100 ? <div className="loading">Loading 3D model... {loadingP}%</div> : null}
        </div>
        <div
          className={`content-wrapper view-${currentView} ${animationPlaying ? 'animating' : ''}`}
        >
          {card.SimpleTextCarousel &&
            card.SimpleTextCarousel.map((elmt, i) => (
              <p className={'scene scene-' + (i + 1)} key={elmt.Key} role={ADAARIARoles.Region} >
                <VenetianBlindsText
                  show={!animationPlaying && card && card.Key === activeSlideKey}
                />
                <span className="target-text">{elmt.Body}</span>
              </p>
            ))}
        </div>
        {card.Link && card.Link.HasLink && card.Link.Text && card.Link.Text.length > 0 && (
          <ExternalLink
            index={card.Key}
            text={card.Link.Text}
            link={
              card.Link.Type === linkTypes.EXTERNAL
                ? card.Link.ExternalURL.Text
                : `/${card.Link.InternalURL.BasePath && card.Link.InternalURL.BasePath.Text}/${
                    card.Link.InternalURL.Path && card.Link.InternalURL.Path.Path
                  }`
            }
            isExternal={card.Link.Type === linkTypes.EXTERNAL}
            isInline={isInline}
          />
        )}
        {isInline && model ? (
          <DotGroup
            ref={controlRef}
            currentSlide={currentView - 1}
            totalSlides={3}
            disableActiveDots={animationPlaying}
            renderDots={renderDots}
          />
        ) : null}
      </>
    );

    return (
      <div className={classname} ref={objectRef} aria-label={'A 3D model'}>
        {isInline ? (
          <Swipeable trackMouse={true} onSwipedLeft={onSwipeLeft} onSwipedRight={onSwipeRight}>
            {inner}
          </Swipeable>
        ) : (
          inner
        )}
      </div>
    );
  }
);

export default Scene3D;
